import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const Policy = () => {
  const navigate = useNavigate();

  // React Router tracks its position in the history stack; if we arrived from
  // somewhere in-app, pop back there, otherwise fall back to the login screen
  // (which forwards logged-in users on to the app).
  const handleBack = () => {
    const idx = window.history.state?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
    } else {
      navigate("/login");
    }
  };

  return (
    <div className="mx-4">
      {/* Installed PWAs have no browser chrome, so this is the only way out of
          this page. Shown on mobile and in any standalone window; desktop
          browsers keep their own back button. */}
      <div className="hidden max-md:flex [@media(display-mode:standalone)]:flex sticky top-0 z-10 -mx-4 px-2 py-2 items-center bg-background/95 backdrop-blur border-b">
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>
      <header className="mt-4 mb-2">
        <h1 className="text-2xl">Privacy Policy for Task Fusion</h1>
      </header>
      <section className="mb-4">
        <p>
          <strong>Effective Date:</strong> 28/11/2025
        </p>
      </section>
      <section>
        <p>
          Welcome to <b>Task Fusion!</b> Your privacy is important to us. This Privacy Policy explains how
          we collect, use, and protect your personal information when you use our app. Please read
          it carefully.
        </p>
      </section>
      <section>
        <h2 className="text-xl mt-4">Limited Use Policy</h2>
        <p>
          Task Fusion's use and transfer of information received from Google APIs to any other app will adhere to <a style={{ textDecorationLine: "underline" }} href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes"> Google API Services User Data Policy</a>, including the Limited Use requirements.
        </p>
      </section>
      <section>
        <h2 className="text-xl mt-4">1. Information We Collect</h2>
        <ul>
          <li>
            <strong>Account Information:</strong> When you use our app, we collect your Google
            username, email address, and profile picture through Google OAuth to personalize and
            manage your experience.
          </li>
          <li>
            <strong>Task Data:</strong> We have access to your Google Tasks via read and write
            scopes. This includes access to your task lists and tasks for viewing, creating,
            editing, and deleting as you use our app.
          </li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl mt-4">2. How We Use Your Information</h2>
        <ul>
          <li>
            To Provide Services: We use the information we collect to operate our app and provide
            you with personalized service. This includes managing your tasks across multiple Google
            accounts.
          </li>
          <li>
            Session Management: We manage your sessions in our app. Your information is used to
            maintain an active session and is not stored beyond this period.
          </li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl mt-4">3. Sharing and Disclosure of Information</h2>
        <p>
          We do not sell, trade, or otherwise transfer your personal information to outside parties. However, we may share your information with the following trusted third parties to assist us in operating our app and serving you:
        </p>
        <ul>
          <li>
            <strong>Service Providers:</strong> We may share your information with third-party vendors, such as cloud hosting providers and analytics services, who assist us in operating our application and providing our services. These parties have access to your personal information only to perform specific tasks on our behalf and are obligated to keep your information confidential.
          </li>
          <li>
            <strong>Legal Compliance:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).
          </li>
        </ul>
      </section>
      <section>
        <h2 className="text-xl mt-4">4. Data Storage and Retention</h2>
        <p>
          We do not store any personal data beyond the duration of your session. All personal data
          is wiped from our system when you log out of our app.
        </p>
      </section>
      <section>
        <h2 className="text-xl mt-4">5. Data Protection</h2>
        <p>
          We take reasonable measures to protect the information we collect from or about you from
          unauthorized access, use, or disclosure. However, no internet or email transmission is
          ever fully secure or error-free.
        </p>
      </section>
      <section>
        <h2 className="text-xl mt-4">6. Your Rights</h2>
        <p>
          You have certain rights regarding the personal information we hold about you. These may
          include the right to access, correct, delete, or restrict the use of your personal
          information, as well as the right to withdraw consent for its use.
        </p>
      </section>
      <section>
        <h2 className="text-xl mt-4">7. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any changes by
          posting the new Privacy Policy on our website. We encourage you to review this Privacy
          Policy periodically for any changes.
        </p>
      </section>
      <section>
        <h2 className="text-xl mt-4">8. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy, please contact us at <a href="mailto:contact@task-fusion.pro">contact@task-fusion.pro</a>.
        </p>
      </section>
    </div>
  );
};

export default Policy;
